// ---------------------------------------------------------------------------
// C1: Content Morphing — "Create from one idea" → 7 platform versions
// ---------------------------------------------------------------------------
// Local drafts until a mounted morph backend exists. Design system applied.
// User edits before scheduling through the mounted CRM social calendar route.
// ---------------------------------------------------------------------------
import { useState } from "react";
import {
  Sparkles,
  Send,
  Edit3,
  Clock,
  Copy,
  Check,
  AlertCircle,
  Camera,
  Briefcase,
  Globe,
  Music2,
  MessageCircle,
  MapPin,
  Play,
  Hash,
} from "lucide-react";
import { useContentMorphing, useScheduleMorphedPost, type MorphPlatformVersion } from "@/hooks/use-content-morphing";
import { useApi } from "@/hooks/use-api";
import { EmptyState } from "@/components/ui/empty-state";

// ---------------------------------------------------------------------------
// Platform Config
// ---------------------------------------------------------------------------

const PLATFORM_CONFIG: Record<string, {
  icon: typeof Camera;
  color: string;
  bgColor: string;
  label: string;
}> = {
  instagram: { icon: Camera, color: "text-[var(--color-ai)]", bgColor: "bg-[var(--color-ai)]/5", label: "Instagram" },
  tiktok: { icon: Music2, color: "text-foreground", bgColor: "bg-[var(--color-surface)]", label: "TikTok" },
  linkedin: { icon: Briefcase, color: "text-[var(--color-primary)]", bgColor: "bg-[var(--color-primary-light)]", label: "LinkedIn" },
  twitter: { icon: MessageCircle, color: "text-sky-500", bgColor: "bg-sky-50", label: "Twitter/X" },
  facebook: { icon: Globe, color: "text-[var(--color-primary)]", bgColor: "bg-[var(--color-primary-light)]", label: "Facebook" },
  pinterest: { icon: MapPin, color: "text-[var(--color-destructive)]", bgColor: "bg-[var(--color-destructive)]/5", label: "Pinterest" },
  youtube: { icon: Play, color: "text-[var(--color-destructive)]", bgColor: "bg-[var(--color-destructive)]/5", label: "YouTube" },
};

const ENGAGEMENT_COLORS = {
  low: "text-[var(--color-muted-foreground)] bg-[var(--color-surface)]",
  medium: "text-[var(--color-warning)] bg-[var(--color-warning)]/5",
  high: "text-[var(--color-success)] bg-[var(--color-success)]/5",
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function MorphingSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="h-64 animate-pulse rounded-xl bg-muted" style={{ animationDelay: `${i * 50}ms` }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platform Version Card
// ---------------------------------------------------------------------------

function VersionCard({
  version,
  onSchedule,
  isScheduling,
}: {
  version: MorphPlatformVersion;
  onSchedule: (v: MorphPlatformVersion) => void;
  isScheduling: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(version.content);
  const [copied, setCopied] = useState(false);

  const config = PLATFORM_CONFIG[version.platform] ?? {
    icon: Globe, color: "text-muted-foreground", bgColor: "bg-muted", label: version.platform,
  };
  const PlatformIcon = config.icon;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="card-hover rounded-xl border border-border bg-background overflow-hidden">
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 ${config.bgColor}`}>
        <div className="flex items-center gap-2">
          <PlatformIcon className={`h-4 w-4 ${config.color}`} />
          <span className="text-sm font-semibold text-foreground">{config.label}</span>
          <span className="text-[10px] text-muted-foreground">{version.format}</span>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${ENGAGEMENT_COLORS[version.estimatedEngagement]}`}>
          {version.estimatedEngagement} engagement
        </span>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        <h4 className="text-sm font-medium text-foreground">{version.title}</h4>

        {isEditing ? (
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className="mt-2 w-full rounded-lg border border-border bg-background p-2 text-xs text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            rows={5}
          />
        ) : (
          <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-5">
            {editedContent}
          </p>
        )}

        {/* Hashtags */}
        {version.hashtags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {version.hashtags.slice(0, 5).map((tag) => (
              <span key={tag} className="inline-flex items-center gap-0.5 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                <Hash className="h-2.5 w-2.5" />
                {tag.replace("#", "")}
              </span>
            ))}
            {version.hashtags.length > 5 && (
              <span className="text-[10px] text-muted-foreground">+{version.hashtags.length - 5}</span>
            )}
          </div>
        )}

        {/* Meta */}
        <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
          <span>{version.characterCount} chars</span>
          {version.optimalPostTime && (
            <span className="flex items-center gap-0.5">
              <Clock className="h-2.5 w-2.5" />
              {version.optimalPostTime}
            </span>
          )}
          <span className="capitalize">{version.suggestedMediaType}</span>
        </div>
      </div>

      {/* AI label + actions */}
      <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
        <div className="flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-[var(--color-ai)]" />
          <span className="text-[10px] text-[var(--color-ai)]">AI-generated</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleCopy}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Copy"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-[var(--color-success)]/500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Edit"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onSchedule({ ...version, content: editedContent })}
            disabled={isScheduling}
            className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors disabled:opacity-50"
          >
            <Send className="h-3 w-3" />
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ContentMorphing() {
  const [idea, setIdea] = useState("");
  const { mutate: morphContent, isLoading: isMorphing, error: morphError } = useContentMorphing();
  const { mutate: schedulePost, isLoading: isScheduling } = useScheduleMorphedPost();
  const [versions, setVersions] = useState<MorphPlatformVersion[]>([]);

  // Brand context from tenant settings API (fetched once, cached)
  const { data: brandSettings } = useApi<{
    businessName: string;
    industry: string;
    tone: string;
    tagline?: string;
    targetAudience?: string;
  }>("/api/platform/brand");

  const handleGenerate = async () => {
    if (!idea.trim()) return;

    const result = await morphContent({
      idea: idea.trim(),
      brandContext: {
        businessName: brandSettings?.businessName ?? "My Business",
        industry: brandSettings?.industry ?? "General",
        tone: brandSettings?.tone ?? "professional",
        tagline: brandSettings?.tagline,
        targetAudience: brandSettings?.targetAudience,
      },
    });

    if (result?.versions) {
      setVersions(result.versions);
    }
  };

  const handleSchedule = async (version: MorphPlatformVersion) => {
    await schedulePost({
      platform: version.platform,
      content: version.content,
      hashtags: version.hashtags,
      mediaType: version.suggestedMediaType,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Content Morphing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          One idea, seven platforms. AI creates native content for each channel.
        </p>
      </div>

      {/* Input */}
      <div className="rounded-xl border border-border bg-background p-4 shadow-sm">
        <label className="block text-sm font-medium text-foreground mb-2">Your content idea</label>
        <textarea
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          placeholder="e.g., Share our teeth whitening before/after results with a special offer..."
          className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          rows={3}
        />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{idea.length} characters</span>
          <button
            onClick={handleGenerate}
            disabled={!idea.trim() || isMorphing}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-ai)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-ai)]/90 transition-colors disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            {isMorphing ? "Generating..." : "Generate 7 Versions"}
          </button>
        </div>
      </div>

      {/* Error */}
      {morphError && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="h-4 w-4" />
          Failed to generate content. Please try again.
        </div>
      )}

      {/* Loading */}
      {isMorphing && <MorphingSkeleton />}

      {/* Results */}
      {!isMorphing && versions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-foreground">
              {versions.length} Platform Versions
            </h2>
            <span className="text-xs text-muted-foreground">Edit any version before scheduling</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {versions.map((v) => (
              <VersionCard
                key={v.platform}
                version={v}
                onSchedule={handleSchedule}
                isScheduling={isScheduling}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state (no generation yet) */}
      {!isMorphing && versions.length === 0 && !idea && (
        <EmptyState
          icon={<Sparkles className="h-8 w-8" />}
          title="Create content for every platform"
          description="Enter one content idea and AI will generate native versions for Instagram, TikTok, LinkedIn, Twitter, Facebook, Pinterest, and YouTube."
          variant="info"
        />
      )}
    </div>
  );
}
